import { ROLES, TYPES, usersUtil } from './users-util';
import { TaskStatusKeys } from '../../domain/task-status-enum';
import { QuotesStatusKeys } from '../../domain/quotes-status-enum';
import TasksChecklist from '../../components/admin/work-orders/details/TasksChecklist';

/**
 * Calculates which button should be visible for a given user and task. Also the quote and task preCondition are important.
 * @param user
 * @param task
 * @param quote
 * @param hasPrecondition
 * @return {{requestBids: boolean, editTask: boolean, createQuote: boolean, askQuestion: boolean,
 * noThanks: boolean, withdrawQuote: boolean, resubmitQuote: boolean, startTask: {enable: boolean, show: boolean},
 * markAsCompleted: {enable: boolean, show: boolean}, approveWork: boolean, showCompleted: boolean}}
 */
export function showDetailsTaskButtons(
  user,
  task,
  quote = null,
  hasPrecondition = null
) {
  const result = {
    requestBids: false,
    editTask: false,
    deleteTask: false,
    createQuote: false,
    askQuestion: false,
    noThanks: false,
    withdrawQuote: false,
    viewCounterQuote: false,
    resubmitQuote: false,
    addMaterials: false,
    addTime: false,
    startTask: {
      enable: false,
      show: false,
    },
    markAsCompleted: {
      enable: false,
      show: false,
    },
    approveWork: false,
    showCompleted: false,
  };
  const gcType = usersUtil.hasType(user, TYPES.GENERAL_CONTRACTOR);

  if (usersUtil.isAdmin(user) || gcType) {
    if (usersUtil.hasRole(user, ROLES.SPACKLE_ADMIN) || gcType) {
      result.requestBids =
        task.status === TaskStatusKeys.RFP ||
        task.status === TaskStatusKeys.PLM ||
        task.status === TaskStatusKeys.AGG;
      result.editTask =
        task.status === TaskStatusKeys.RFP ||
        task.status === TaskStatusKeys.PLM ||
        task.status === TaskStatusKeys.AGG ||
        task.status === TaskStatusKeys.WOA;
      result.deleteTask =
        task.status === TaskStatusKeys.RFP ||
        task.status === TaskStatusKeys.PLM ||
        task.status === TaskStatusKeys.AGG ||
        task.status === TaskStatusKeys.WOA;
    }

    if (
      usersUtil.hasRole(user, ROLES.SPACKLE_ADMIN) ||
      usersUtil.hasRole(user, ROLES.FIELD) ||
      gcType
    ) {
      result.approveWork =
        task.status === TaskStatusKeys.CMP && !task.cmp_approved;
    }

    result.showCompleted =
      (task.status === TaskStatusKeys.CMP ||
        task.status === TaskStatusKeys.CLS) &&
      task.cmp_approved;

    if (!task.awardedContractor) {
      result.createQuote =
        (task.status === TaskStatusKeys.RFP ||
          task.status === TaskStatusKeys.AGG ||
          task.status === TaskStatusKeys.PLM) &&
        !quote;
      result.withdrawQuote =
        (task.status === TaskStatusKeys.RFP ||
          task.status === TaskStatusKeys.AGG ||
          task.status === TaskStatusKeys.PLM ||
          task.status === TaskStatusKeys.WOA) &&
        quote &&
        quote.status === QuotesStatusKeys.RECEIVED;
      result.resubmitQuote =
        (task.status === TaskStatusKeys.RFP ||
          task.status === TaskStatusKeys.AGG ||
          task.status === TaskStatusKeys.PLM ||
          task.status === TaskStatusKeys.WOA) &&
        quote &&
        quote.status === QuotesStatusKeys.WITHDRAWN;

      result.startTask.show = task.status === TaskStatusKeys.PRE;
      result.startTask.enable =
        result.startTask.show &&
        startTaskConditionsMet(false, task, hasPrecondition);

      result.markAsCompleted.show = task.status === TaskStatusKeys.WIP;
      result.markAsCompleted.enable =
        result.markAsCompleted.show &&
        task.task_checklist.completed_images.length > 0;
    }
  } else if (usersUtil.hasType(user, TYPES.CONTRACTOR)) {
    if (quote !== null) {
      result.createQuote =
        (task.status === TaskStatusKeys.RFP ||
          task.status === TaskStatusKeys.PLM) &&
        quote.status === QuotesStatusKeys.WAITING;
      result.askQuestion = result.createQuote;
      result.noThanks = result.createQuote;

      result.withdrawQuote =
        (task.status === TaskStatusKeys.RFP ||
          task.status === TaskStatusKeys.PLM ||
          task.status === TaskStatusKeys.WOA) &&
        quote.status === QuotesStatusKeys.RECEIVED;
      result.viewCounterQuote =
        result.withdrawQuote && quote.counter_status === 'negotiating';
      result.resubmitQuote =
        (task.status === TaskStatusKeys.RFP ||
          task.status === TaskStatusKeys.PLM ||
          task.status === TaskStatusKeys.WOA) &&
        quote.status === QuotesStatusKeys.WITHDRAWN;
    }

    result.addMaterials =
      task.status === TaskStatusKeys.PRE ||
      task.status === TaskStatusKeys.WIP ||
      task.status === TaskStatusKeys.CMP ||
      task.status === TaskStatusKeys.CLS;
    result.addTime =
      task.status === TaskStatusKeys.CMP || task.status === TaskStatusKeys.CLS;

    result.startTask.show = task.status === TaskStatusKeys.PRE;
    result.startTask.enable =
      result.startTask.show &&
      startTaskConditionsMet(false, task, hasPrecondition);

    result.markAsCompleted.show = task.status === TaskStatusKeys.WIP;
    result.markAsCompleted.enable =
      result.markAsCompleted.show &&
      task.task_checklist.completed_images.length > 0;

    result.showCompleted =
      (task.status === TaskStatusKeys.CMP ||
        task.status === TaskStatusKeys.CLS) &&
      task.cmp_approved;
  } else if (usersUtil.hasType(user, TYPES.CLIENT)) {
    result.approveWork =
      task.status === TaskStatusKeys.CMP && !task.cmp_approved;

    result.showCompleted =
      (task.status === TaskStatusKeys.CMP ||
        task.status === TaskStatusKeys.CLS) &&
      task.cmp_approved;

    result.editTask =
      task.status === TaskStatusKeys.RFP || task.status === TaskStatusKeys.AGG;
    result.deleteTask = result.editTask;
  }

  return result;
}

/**
 * Check if the task checklist is complete.
 * @param task
 * @return {boolean}
 */
export function isTaskChecklistComplete(task = null) {
  if (
    task !== null &&
    task.task_checklist !== undefined &&
    task.task_checklist !== null
  ) {
    // All keys should be true
    return (
      TasksChecklist.keys.filter((key) => !task.task_checklist[key]).length ===
      0
    );
  }
  return false;
}

function startTaskConditionsMet(isAdmin, task, hasPrecondition) {
  if (isAdmin && isTaskChecklistComplete(task)) {
    return true;
  } else if (
    !isAdmin &&
    isTaskChecklistComplete(task) &&
    hasPrecondition !== null
  ) {
    // precondition has to be defined
    return (
      !hasPrecondition ||
      (hasPrecondition &&
        task.task_checklist.pre_existing_images.length > 0 &&
        task.task_checklist.pre_existing_desc !== null &&
        task.task_checklist.pre_existing_desc !== '')
    );
  } else {
    return false;
  }
}



// WEBPACK FOOTER //
// ./src/services/utils/details-task-util.js